import { useEffect, type FC, useState } from "react";
import classNames from "classnames";
import { MyLoan } from "./MyLoan";
import { EmptyLoan } from "./EmptyLoan";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { generalLiterals, healthFactorDescription } from "../../../literals";
import { useCustomWallet } from "../../providers";
import { Loan } from "../../../logic/types/loan/Loan";
import borrowModule from "../../../logic/BorrowModule";
import { useErrorAlert } from "../../../logic/hooks";
import { Address } from "viem";
import { handleErrors } from "../../../errors";
import LoanFactory from "../../../logic/types/loan/LoanFactory";
import { Info } from "../../Info";
import NftFactory from "../../../logic/types/nft/NftFactory";
import { areTheSameNft } from "@unlockdfinance/verislabs-web3/utils";
import { BasicProps } from "components/types/BasicProps";
import { INft } from "logic/types/nft/INft";
import { MarketItemType } from "contracts/MarketContract";

type Props = BasicProps & {
  availableNfts: INft[];
  onFullyRepaid?: (loan: Loan) => void;
  onNftSold?: (nft: INft) => void;
};

export const MyLoans: FC<Props> = (props) => {
  const { className, availableNfts, onFullyRepaid, onNftSold } = props;
  const [isLoading, setLoading] = useState<boolean>(true);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [error, setError] = useState<Error | null>(null);
  useErrorAlert(error);
  const { address } = useCustomWallet();

  useEffect(() => {
    if (address) {
      handleGetLoans(address);

      return () => {
        setLoans([]);
      };
    }
  }, [address]);

  const handleGetLoans = async (address: Address) => {
    try {
      setLoading(true);
      setLoans(await borrowModule.getLoansOfAddress(address));
    } catch (err) {
      setError(handleErrors(err));
    } finally {
      setLoading(false);
    }
  };

  const handleNftSold = (loanId: Address, nft: INft) => {
    if (address) {
      // WE WAIT HERE TO BE SURE THAT THE PROVIDER WILL READ THE CORRECT BLOCK
      setTimeout(async () => {
        try {
          const [currentBorrowAmount] =
            await borrowModule.getCurrentBorrowAmounts([
              { id: loanId, underlyingAsset: nft.currency.address },
            ]);

          setLoans((loans) => {
            const loansCopy = [...loans];

            const index = loans.findIndex(({ id }) => id === loanId);

            if (index !== -1) {
              if (currentBorrowAmount === BigInt(0)) {
                const loan = loansCopy[index];

                onFullyRepaid?.(loan);

                loansCopy.splice(index, 1);
              } else {
                const nfts = loansCopy[index].nfts.filter(
                  (_nft) => !areTheSameNft(_nft, nft)
                );

                loansCopy[index] = LoanFactory.create({
                  ...loans[index],
                  currentBorrowAmount,
                  nfts,
                });
              }
            }

            return loansCopy;
          });
        } catch (err) {
          setError(handleErrors(err));
        }
      }, 10000);
    }

    onNftSold?.(nft);
  };

  const handleRedeemed = (loanId: Address) => {
    setLoans((loans) => {
      const loansCopy = [...loans];

      const index = loans.findIndex(({ id }) => id === loanId);

      if (index !== -1) {
        const loan = LoanFactory.create({
          ...loans[index],
          currentBorrowAmount: BigInt(0),
          nfts: loans[index].nfts.map((nft) =>
            NftFactory.create({
              ...nft,
              collection: nft.collection,
              marketItemData:
                nft.marketItemData?.type !==
                MarketItemType.TYPE_LIQUIDATION_AUCTION
                  ? nft.marketItemData
                  : undefined,
            })
          ),
        });

        loansCopy[index] = loan;
      }

      return loansCopy;
    });
  };

  const handleNftUnlisted = (loanId: Address, nft: INft) => {
    setLoans((prevLoans) => {
      const loansCopy = [...prevLoans];

      const loanIndex = loansCopy.findIndex(({ id }) => id === loanId);

      if (loanIndex !== -1) {
        const nftIndex = loansCopy[loanIndex].nfts.findIndex((_nft) =>
          areTheSameNft(_nft, nft)
        );

        if (nftIndex !== -1) {
          const nftsCopy = [...loansCopy[loanIndex].nfts];
          nftsCopy[nftIndex] = NftFactory.create({
            ...nftsCopy[nftIndex],
            marketItemData: undefined,
            collection: nftsCopy[nftIndex].collection,
          });

          loansCopy[loanIndex] = LoanFactory.create({
            ...loansCopy[loanIndex],
            nfts: nftsCopy,
          });
        }
      }

      return loansCopy;
    });
  };

  return (
    <>
      <section
        className={classNames(
          "overflow-visible max-w-7xl mx-auto relative rounded-xl p-0.5 border-unlockd",
          [className]
        )}
      >
        <div className="w-full bg-secondary rounded-xl pt-[30px] pr-0 pb-[43px] pl-[25px]">
          <h2 className="mb-[14px] flex text-2xl font-bold">
            {generalLiterals.myLoans.title}
          </h2>
          <table className="w-full text-base text-left text-white overflow-y-visible">
            <thead className="text-md">
              <tr>
                <th scope="col" className="text-xs py-3 pl-1 pr-3">
                  {generalLiterals.myLoans.nftsLabel}
                </th>
                <th scope="col" className="text-xs py-3 px-3">
                  {generalLiterals.myLoans.loanIdLabel}
                </th>
                <th scope="col" className="text-xs py-3 px-3">
                  {generalLiterals.myLoans.valuationLabel}
                </th>
                <th scope="col" className="text-xs py-3 px-3">
                  <span className="block">
                    {generalLiterals.myLoans.liquidationPriceLabelL1}
                  </span>
                  <span>{generalLiterals.myLoans.liquidationPriceLabelL2}</span>
                </th>
                <th
                  scope="col"
                  className="text-xs py-3 flex flex-col px-3 pt-3"
                >
                  <span>{generalLiterals.myLoans.healthFactorLabelL1}</span>
                  <div className="flex gap-1">
                    <span>{generalLiterals.myLoans.healthFactorLabelL2}</span>
                    <Info>
                      <Info.Description>
                        {healthFactorDescription}
                      </Info.Description>
                    </Info>
                  </div>
                </th>
                <th scope="col" className="text-xs py-3 px-3">
                  <span className="block">Borrowed</span>
                  <span>Amount</span>
                </th>
                <th scope="col" className="text-xs py-3 px-3">
                  <span className="block">
                    {generalLiterals.myLoans.avToBorrowLabelL1}
                  </span>
                  <span>{generalLiterals.myLoans.avToBorrowLabelL2}</span>
                </th>
                <th scope="col" className="w-40 text-xs py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                loans.length > 0 &&
                loans.map((loan, index) => (
                  <MyLoan
                    className={classNames(
                      "overflow-y-visible",
                      loans[index + 1] ? "border-b border-b-[#FFF6F6]" : ""
                    )}
                    key={loan.id}
                    loan={loan}
                    onRedeemed={handleRedeemed}
                    availableNfts={availableNfts}
                    onSold={handleNftSold}
                    onNftUnlisted={handleNftUnlisted}
                  />
                ))}
              {!isLoading && loans.length === 0 && <EmptyLoan />}
            </tbody>
          </table>
          {isLoading && (
            <div className="my-4 w-full flex justify-center py-4">
              <AiOutlineLoading3Quarters className="animate-spin w-10 h-10" />
            </div>
          )}
        </div>
      </section>
    </>
  );
};
